import type { GrantedItems } from "@highmarch/domain";
import { toast } from "../toast";
import { econCue, flyCurrency, purchasePop } from "../hub/purchaseFx";
import type { DealsLink } from "./dealsLink";
import type { CardsCtx } from "./cardsPage";
import { blockedText, itemTiles } from "./dealsModel";
import { revealThenReceipt } from "./receipt";

/**
 * What a tap on a card's CLAIM, a fund milestone or the hub's Cards chip does (MP-01/02/03): the claim through
 * @highmarch/app `Cards` (hub only), then the free-claim receipt the VIP daily bundle uses — the cue, the card's pop, gems
 * flying to the pill, "FREE REWARD"-style reveal and receipt. Shared by the Deals screen and the hub chip.
 */

/** How long the claimed card glows before its page redraws (dealsSheet.ts REVEAL_MS). */
const REVEAL_MS = 700;

/** The Cards pages' views now. */
export function cardsCtx(l: DealsLink): CardsCtx {
  const c = l.deals.cards, fund = c.fundDefs()[0];
  return { cards: c.views(l.now()), fund: fund ? c.fundView(fund.id) : null };
}

/** The Deals screen drew `pageId`: opening the Fund page clears its one-time "new" badge. */
export function cardsPageShown(l: DealsLink, pageId: string | null): void {
  if (pageId === "fund" && l.deals.cards.markFundSeen()) l.changed();
}

function delivered(l: DealsLink, parent: HTMLElement, title: string, g: GrantedItems, from: Element | null, after?: () => void): void {
  econCue("buy");
  const before = l.catalog.economy.wallet.gems - g.gems;
  l.changed();
  purchasePop(from);
  if (g.gems > 0) flyCurrency(from, "gems", before, l.catalog.economy.wallet.gems);
  if (after) window.setTimeout(after, REVEAL_MS);
  revealThenReceipt(parent, title, g, []);
}

/** The hub chip: claims every card's waiting days in one receipt. False when nothing waited. */
export function claimAllCards(l: DealsLink, parent: HTMLElement, from: Element | null): boolean {
  const r = l.deals.cards.claimAll(l.now());
  if (typeof r === "string") { if (r !== "nothing to claim") toast(parent, blockedText(r)); return false; }
  delivered(l, parent, r.names.length === 1 ? r.names[0].toUpperCase() : "CARD REWARDS", r.granted, from);
  return true;
}

/** Binds a Cards or Fund page's CLAIM buttons and milestone stops (the Deals screen calls it after each draw). */
export function bindCardsPage(root: HTMLElement, l: DealsLink, parent: HTMLElement, rerender: () => void): void {
  for (const b of root.querySelectorAll<HTMLButtonElement>(".dcclaim[data-card]")) {
    b.onclick = () => {
      const id = b.dataset.card!, r = l.deals.cards.claim(id, l.now());
      if (typeof r === "string") { toast(parent, blockedText(r)); rerender(); return; }
      delivered(l, parent, (l.deals.cards.view(id, l.now())?.name ?? "Card").toUpperCase(), r, b.closest(".doffer"), () => { if (root.isConnected) rerender(); });
    };
  }
  for (const b of root.querySelectorAll<HTMLButtonElement>(".fstop[data-level]")) b.onclick = () => fundStop(l, parent, b, rerender);
}

function fundStop(l: DealsLink, parent: HTMLElement, stop: HTMLElement, rerender: () => void): void {
  const cards = l.deals.cards, def = cards.fundDefs()[0], level = Number(stop.dataset.level);
  const m = def?.fund?.milestones.find((x) => x.level === level);
  if (!def || !m) return;
  if (!stop.classList.contains("s-ready")) {
    const names = itemTiles(l.catalog.balance, m.items, l.deals.level).map((t) => t.name).join(", ");
    const why = stop.classList.contains("s-claimed") ? `Level ${level}: ${names} · claimed`
      : stop.classList.contains("s-reached") ? `Level ${level} cleared: ${names} the moment you buy` : `Clear level ${level}: ${names}`;
    toast(parent, why);
    return;
  }
  const r = cards.claimFund(def.id, level, l.now());
  if (typeof r === "string") { toast(parent, blockedText(r)); rerender(); return; }
  delivered(l, parent, def.name.toUpperCase(), r, stop, () => rerender());
}
